import G6 from '@antv/g6';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import React from 'react';

import { baseCommands } from '../../../../commands';
import {
  FLOW_CONTAINER_ID,
  GraphBehavior,
  GraphCanvasEvent,
  GraphCommonEvent,
  GraphCustomEvent,
  GraphEdgeEvent,
  GraphMode,
  GraphNodeEvent,
} from '../../../../constants';
import { Global } from '../../../../global';
import { EditorPrivateContextProps, withEditorPrivateContext } from '../../../../hooks';
import { EDGE_TYPES, NODE_TYPES, edgeStateStyles } from '../../../../shapes';
import {
  FlowData,
  Graph,
  GraphEvent,
  GraphNativeEvent,
  GraphOptions,
  GraphReactEvent,
  GraphReactEventProps,
  Item,
  Modes,
} from '../../../../types';
import { guid, isCanvasMovable, setGlobal } from '../../../../utils';
import { getCreateEdgeConfig } from './G6Graph.utils';

import '../../../../behaviors/dragAddNode';
import '../../../../behaviors/hoverItem';
import '../../../../behaviors/recallEdge';
import '../../../../behaviors/doubleClickNode';

export interface G6GraphProps extends Partial<GraphReactEventProps>, EditorPrivateContextProps {
  style?: React.CSSProperties;
  className?: string;
  data: FlowData;
  graphConfig?: Partial<GraphOptions>;
  children: React.ReactNode;
}

class G6GraphComponent extends React.Component<G6GraphProps> {
  static defaultProps = {
    graphConfig: {},
  };

  graph: Graph | null = null;
  containerId = `${FLOW_CONTAINER_ID}_${guid()}`;
  cleanupEvents: (() => void)[] = [];

  componentDidMount() {
    this.init();
    this.bindEvent();
  }

  componentDidUpdate(prevProps: G6GraphProps) {
    const { data } = this.props;

    if (!isEqual(data, prevProps.data)) {
      this.changeData(data);
    }
  }

  componentWillUnmount() {
    this.cleanupEvents.forEach((fn) => typeof fn === 'function' && fn());
    this.graph = null;
  }

  canDragNode = (e: GraphEvent) => {
    const { graph } = this;

    // Stops dragging when overlay is visible to avoid bugs
    if (graph?.get('isOverlayVisible') === true) return false;

    const shapeName = e?.target?.get('className') || e?.target?.get('name') || '';
    // Do not move the node when the target is an anchor
    return !shapeName.toLowerCase().includes('anchor');
  };

  canDragOrZoomCanvas = () => isCanvasMovable(this.graph);

  parseData = (data: FlowData = {}) => {
    const { nodes = [], edges = [] } = data;
    [...nodes, ...edges].forEach((item) => {
      const { id } = item;

      if (id) return;

      item.id = guid();
    });
  };

  getBehaviors = () => ({
    [GraphBehavior.BrushSelect]: GraphBehavior.BrushSelect,
    /**
     * When a node is clicked, select it
     * Multiselect with "Shift"
     * https://g6.antv.vision/en/docs/manual/middle/states/defaultBehavior#click-select
     */
    [GraphBehavior.ClickSelect]: GraphBehavior.ClickSelect,
    [GraphBehavior.CreateEdge]: getCreateEdgeConfig(),
    [GraphBehavior.DragAddNode]: GraphBehavior.DragAddNode,
    [GraphBehavior.DragCanvas]: {
      type: GraphBehavior.DragCanvas,
      // performance enhancement
      enableOptimize: false,
      shouldBegin: this.canDragOrZoomCanvas,
      shouldUpdate: this.canDragOrZoomCanvas,
    },
    [GraphBehavior.DragCombo]: GraphBehavior.DragCombo,
    [GraphBehavior.DragNode]: {
      type: GraphBehavior.DragNode,
      // remove 'shadow' on drag
      enableDelegate: false,
      shouldBegin: this.canDragNode,
      onlyChangeComboSize: true,
    },
    [GraphBehavior.HoverItem]: {
      type: GraphBehavior.HoverItem,
      // Force update on note node since there is a size change
      shouldForceUpdateOnHover: (item: Item) => item?.getModel()?.type === NODE_TYPES.NOTE,
    },
    [GraphBehavior.RecallEdge]: GraphBehavior.RecallEdge,
    [GraphBehavior.ScrollCanvas]: {
      type: GraphBehavior.ScrollCanvas,
      zoomKey: 'ctrl',
      shouldUpdate: this.canDragOrZoomCanvas,
    },
    [GraphBehavior.ZoomCanvas]: {
      type: GraphBehavior.ZoomCanvas,
      sensitivity: 1,
      shouldUpdate: this.canDragOrZoomCanvas,
    },
    [GraphBehavior.DoubleClickNode]: GraphBehavior.DoubleClickNode,
  });

  getModes = (): Modes => {
    const behaviors = this.getBehaviors();
    return {
      default: [
        behaviors[GraphBehavior.BrushSelect],
        behaviors[GraphBehavior.ClickSelect],
        behaviors[GraphBehavior.CreateEdge],
        behaviors[GraphBehavior.DoubleClickNode],
        behaviors[GraphBehavior.DragCanvas],
        behaviors[GraphBehavior.DragNode],
        behaviors[GraphBehavior.HoverItem],
        behaviors[GraphBehavior.RecallEdge],
        // @ts-ignore
        behaviors[GraphBehavior.ScrollCanvas],
        behaviors[GraphBehavior.DragCombo],
      ],
      [GraphMode.AddNode]: [
        behaviors[GraphBehavior.DoubleClickNode],
        behaviors[GraphBehavior.DragAddNode],
      ],
      [GraphMode.OverlayVisible]: [
        behaviors[GraphBehavior.DoubleClickNode],
        behaviors[GraphBehavior.DragNode],
      ],
      [GraphMode.Readonly]: [
        behaviors[GraphBehavior.DoubleClickNode],
        behaviors[GraphBehavior.DragCanvas],
        // @ts-ignore
        behaviors[GraphBehavior.ScrollCanvas],
        behaviors[GraphBehavior.HoverItem],
        behaviors[GraphBehavior.ClickSelect],
        behaviors[GraphBehavior.BrushSelect],
      ],
      [GraphMode.JourneyAssistActive]: [
        behaviors[GraphBehavior.DragCanvas],
        // @ts-ignore
        behaviors[GraphBehavior.ScrollCanvas],
      ],
    };
  };

  initGraph = (width: number, height: number) => {
    const { containerId } = this;
    const { graphConfig } = this.props;

    this.graph = new G6.Graph({
      container: containerId,
      width,
      height,
      modes: this.getModes(),
      defaultNode: { type: NODE_TYPES.BASE },
      defaultEdge: { type: EDGE_TYPES.DEFAULT },
      /**
       * State styles for edges
       * There is some bugginess when applying this in the edge custom
       * config, and it was solved when moved here.
       * See: https://iterable.atlassian.net/browse/WF-641
       */
      edgeStateStyles,
      ...graphConfig,
    });

    return this.graph;
  };

  init() {
    const { setGraph, commandManager } = this.props;
    const { clientWidth = 0, clientHeight = 0 } = document.getElementById(this.containerId) || {};

    // Analytical data
    const data = { ...this.props.data };

    this.parseData(data);

    // Initial painting
    this.graph = this.initGraph(clientWidth, clientHeight);

    this.graph.data(data);
    this.graph.render();
    /**
     * If we begin to support TreeGraphs, we need to focus the root node around here
     * See line 80 of the following:
     * https://github.com/alibaba/GGEditor/blob/master/src/components/Graph/index.tsx
     */
    this.graph.setMode('default');

    setGraph(this.graph);

    // Set up the command manager
    this.graph.set('commandManager', commandManager);

    // set our iterable specific configuration
    setGlobal(this.graph, new Global());

    // Register command
    const commands: Record<string, any> = baseCommands;
    Object.keys(commands).forEach((name) => {
      commandManager.register(name, commands[name]);
    });
  }

  bindEvent() {
    const { graph, props } = this;

    if (!graph) return;

    const events: {
      [propName in GraphReactEvent]: GraphNativeEvent;
    } = {
      ...GraphCommonEvent,
      ...GraphNodeEvent,
      ...GraphEdgeEvent,
      ...GraphCanvasEvent,
      ...GraphCustomEvent,
    };

    (Object.keys(events) as GraphReactEvent[]).forEach((eventName) => {
      const event = props[eventName];

      if (typeof event === 'function') {
        // REVIEW: Does this need to be cleaned up?
        // @ts-ignore
        graph.on(events[eventName], event);
        this.cleanupEvents.push(() => graph.off(events[eventName], event));
      }
    });
  }

  changeData(data: any) {
    const { graph } = this;

    if (!graph) return;

    this.parseData(data);
    graph.changeData(data);
    /**
     * If we begin to support TreeGraphs, we need to focus the root node around here
     * See line 145 of the following:
     * https://github.com/alibaba/GGEditor/blob/master/src/components/Graph/index.tsx
     */
  }

  render() {
    return (
      <div id={this.containerId} {...pick(this.props, ['className', 'style'])}>
        {this.props.children}
      </div>
    );
  }
}

export const G6Graph = withEditorPrivateContext(G6GraphComponent);
export default G6Graph;
